import React from 'react';
import { Form } from 'react-bootstrap';
import { RiDeleteBin6Line } from '@react-icons/all-files/ri/RiDeleteBin6Line';
import './styles.scss';
import { DropdownComponent } from 'Components/molecules';
import {
    CONDITION_TYPES,
    CondEntry,
    ConditionOperator,
    ConditionType,
    OPERATORS_BY_TYPE,
} from 'Core-utils/types/rules.types';
import { isArrayOperator } from 'Core-utils/helpers/ruleBuilderHelpers';
import {  ErrorIcon, ValueInput } from 'Components/molecules/ValueInput';

export interface ConditionRowProps {
    cond: CondEntry;
    onChange: (patch: Partial<CondEntry>) => void;
    onRemove: () => void;
    showDragHandle?: boolean;
}

const isFieldPathInvalid = (fieldPath: string | undefined): boolean => {
    const trimmed = fieldPath?.trim() ?? '';
    return trimmed.length === 0 || trimmed === '__custom__';
};

const ConditionRow = ({ cond, onChange, onRemove, showDragHandle }: ConditionRowProps) => {
    const operators = OPERATORS_BY_TYPE[cond.fieldType] ?? OPERATORS_BY_TYPE.string;
    const fieldPathInvalid = isFieldPathInvalid(cond.fieldPath);

    const handleTypeChange = (type: string | null) => {
        const newType = (type ?? 'string') as ConditionType;
        onChange({ fieldType: newType, operator: OPERATORS_BY_TYPE[newType][0].value, value: '' });
    };

    const handleOpChange = (op: string | null) => {
        const newOp = (op ?? 'equal_to') as ConditionOperator;
        const newIsArr = isArrayOperator(newOp);
        const curIsArr = isArrayOperator(cond.operator);
        let safeValue = cond.value;
        if (newIsArr && !curIsArr) {
            safeValue = cond.value ? [String(cond.value)] : [];
        }
        if (!newIsArr && curIsArr) {
            safeValue = Array.isArray(cond.value) && cond.value.length ? String(cond.value[0]) : '';
        }
        onChange({ operator: newOp, value: safeValue });
    };

    return (
        <div className="rb-cond-row">
            {showDragHandle && <span className="rb-cond-row__drag" title="Drag to reorder">⠿</span>}

            {/* Type */}
            <div className="rb-cond-row__type rb-dropdown-wrap">
                <DropdownComponent
                    value={CONDITION_TYPES.find((t) => t.value === cond.fieldType)?.label ?? ''}
                    dropDownItems={CONDITION_TYPES.map((t) => ({ id: t.value, displayValue: t.label, value: t.value }))}
                    onChange={handleTypeChange}
                    variant="filled"
                />
            </div>

            {/* Field path — free text */}
            <div className="rb-cond-row__field rb-input-icon-wrap">
                <Form.Control
                    className="rb-input-small"
                    value={cond.fieldPath}
                    placeholder="field path"
                    isInvalid={fieldPathInvalid}
                    onChange={(e) => onChange({ fieldPath: e.target.value })}
                    style={{ fontFamily: 'monospace' }}
                />
                {fieldPathInvalid && <ErrorIcon message="Field path is required" />}
            </div>

            {/* Operator */}
            <div className="rb-cond-row__op rb-dropdown-wrap">
                <DropdownComponent
                    value={operators.find((o) => o.value === cond.operator)?.label ?? ''}
                    dropDownItems={operators.map((o) => ({ id: o.value, displayValue: o.label, value: o.value }))}
                    onChange={handleOpChange}
                    variant="filled"
                />
            </div>

            {/* Value */}
            <div className="rb-cond-row__value">
                <ValueInput cond={cond} onChange={onChange} />
            </div>

            {/* Delete */}
            <button type="button" className="rb-cond-row__del" onClick={onRemove} title="Remove condition">
                <RiDeleteBin6Line size={15} />
            </button>
        </div>
    );
};

export default ConditionRow;
